import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarClock, ListChecks, AlertTriangle, Plus, Mail, X } from "lucide-react";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { useAuth } from "@/lib/auth/auth-provider";
import { useRole } from "@/lib/auth/use-role";
import {
  Badge,
  Card,
  Dot,
  EmptyState,
  PageHeader,
  Button,
  Spinner,
} from "@/components/ui/primitives";
import {
  listCalendar,
  sendComplianceDigest,
  type CalendarRow,
  type DigestResult,
} from "@/lib/api/compliance";
import { listFunds } from "@/lib/api/funds";
import { listLps } from "@/lib/api/lps";
import {
  AUTHORITY_LABEL,
  CATEGORY_LABEL,
  SOURCE_LABEL,
  STATUS_LABEL,
  STATUS_TONE,
  URGENCY_LABEL,
  URGENCY_TONE,
} from "@/lib/compliance-labels";
import { taskUrgency, type TaskUrgency } from "@argus/engine";

const TODAY = new Date().toISOString().slice(0, 10);

const BUCKET_ORDER: TaskUrgency[] = ["overdue", "due_soon", "upcoming", "scheduled", "done"];

function daysUntil(dueIso: string | null): string {
  if (!dueIso) return "—";
  const d = Math.round(
    (new Date(`${dueIso}T00:00:00Z`).getTime() - new Date(`${TODAY}T00:00:00Z`).getTime()) /
      86_400_000,
  );
  if (d === 0) return "today";
  if (d < 0) return `${-d}d ago`;
  return `in ${d}d`;
}

function CalendarInner() {
  const tenant = useTenant();
  const { user } = useAuth();
  const { canWrite, role } = useRole(tenant.id);
  const canDigest = canWrite || role === "reviewer";
  const [showDone, setShowDone] = useState(false);
  const [catFilter, setCatFilter] = useState<string>("all");
  const [srcFilter, setSrcFilter] = useState<string>("all");
  const [digest, setDigest] = useState<DigestResult | null>(null);

  const sendDigest = useMutation({
    mutationFn: () => sendComplianceDigest(tenant.id),
    onSuccess: setDigest,
  });

  const {
    data: rows,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["compliance-calendar", tenant.id],
    queryFn: () => listCalendar(tenant.id),
  });
  const { data: funds } = useQuery({
    queryKey: ["funds", tenant.id],
    queryFn: () => listFunds(tenant.id),
  });
  const { data: lps } = useQuery({
    queryKey: ["lps", tenant.id],
    queryFn: () => listLps(tenant.id),
  });

  const fundName = (id: string | null) =>
    id ? (funds?.find((f) => f.id === id)?.name ?? "—") : null;
  const lpName = (id: string | null) =>
    id ? (lps?.find((l) => l.id === id)?.legal_name ?? "—") : null;

  const enriched = useMemo(
    () =>
      (rows ?? []).map((r) => ({
        row: r,
        urgency: taskUrgency(r.due_date, r.lead_time_days, r.status, TODAY),
      })),
    [rows],
  );

  const filtered = enriched.filter(({ row }) => {
    if (catFilter !== "all" && row.category !== catFilter) return false;
    if (srcFilter !== "all" && row.source !== srcFilter) return false;
    return true;
  });

  const counts = useMemo(() => {
    const c: Record<TaskUrgency, number> = {
      overdue: 0,
      due_soon: 0,
      upcoming: 0,
      scheduled: 0,
      done: 0,
    };
    for (const e of enriched) c[e.urgency]++;
    return c;
  }, [enriched]);

  const buckets = useMemo(() => {
    const b: Record<TaskUrgency, typeof filtered> = {
      overdue: [],
      due_soon: [],
      upcoming: [],
      scheduled: [],
      done: [],
    };
    for (const e of filtered) b[e.urgency].push(e);
    return b;
  }, [filtered]);

  const STATS = [
    { label: "Overdue", value: counts.overdue, tone: "breach" as const },
    { label: "Due soon", value: counts.due_soon, tone: "risk" as const },
    { label: "Upcoming (30d)", value: counts.upcoming, tone: "info" as const },
    {
      label: "Open total",
      value: counts.overdue + counts.due_soon + counts.upcoming + counts.scheduled,
      tone: "neutral" as const,
    },
  ];

  return (
    <>
      <PageHeader
        title="Compliance calendar"
        subtitle="Every dated compliance duty — regulatory filings, attestations, and side-letter obligations — in one place, with lead-time alerts and owners."
        actions={
          <div className="flex items-center gap-2">
            {canDigest && (
              <Button
                variant="secondary"
                disabled={sendDigest.isPending}
                onClick={() => sendDigest.mutate()}
                title="Email a lead-time reminder digest"
              >
                {sendDigest.isPending ? (
                  <Spinner className="size-4" />
                ) : (
                  <Mail className="size-4" />
                )}
                Email reminders
              </Button>
            )}
            <Link to="/t/$slug/requirements" params={{ slug: tenant.slug }}>
              <Button variant="secondary">
                <ListChecks className="size-4" /> Manage requirements
              </Button>
            </Link>
          </div>
        }
      />

      {sendDigest.error && (
        <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
          {sendDigest.error.message}
        </div>
      )}
      {digest && <DigestResultBanner digest={digest} onClose={() => setDigest(null)} />}

      {/* Summary tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {STATS.map((s) => (
          <Card key={s.label} className="px-4 py-3">
            <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-neutral-500">
              <Dot tone={s.tone} /> {s.label}
            </div>
            <div className="mt-1 text-2xl font-semibold tabular-nums text-neutral-50">
              {s.value}
            </div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <select
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          className="rounded-md border border-neutral-800 bg-neutral-900/60 px-2 py-1.5 text-neutral-300"
        >
          <option value="all">All categories</option>
          {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <select
          value={srcFilter}
          onChange={(e) => setSrcFilter(e.target.value)}
          className="rounded-md border border-neutral-800 bg-neutral-900/60 px-2 py-1.5 text-neutral-300"
        >
          <option value="all">All sources</option>
          {Object.entries(SOURCE_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <label className="ml-auto flex items-center gap-2 text-neutral-400">
          <input
            type="checkbox"
            checked={showDone}
            onChange={(e) => setShowDone(e.target.checked)}
            className="accent-neutral-400"
          />
          Show completed
        </label>
      </div>

      {isLoading ? (
        <p className="text-sm text-neutral-500">Loading calendar…</p>
      ) : error ? (
        <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
          {error.message}
        </div>
      ) : enriched.length === 0 ? (
        <EmptyState
          icon={<CalendarClock className="size-7" />}
          title="No compliance items yet"
          description="Add requirements from the regulatory catalog (Form ADV, Form PF, code of ethics, and more) or let extraction surface side-letter obligations — they all land here."
          action={
            <Link to="/t/$slug/requirements" params={{ slug: tenant.slug }}>
              <Button variant="primary">
                <Plus className="size-4" /> Add requirements
              </Button>
            </Link>
          }
        />
      ) : (
        BUCKET_ORDER.filter((b) => (b === "done" ? showDone : true)).map((bucket) =>
          buckets[bucket].length === 0 ? null : (
            <section key={bucket} className="space-y-2">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                {bucket === "overdue" && <AlertTriangle className="size-4 text-red-400" />}
                <span
                  className={
                    bucket === "overdue"
                      ? "text-red-300"
                      : bucket === "due_soon"
                        ? "text-amber-300"
                        : bucket === "upcoming"
                          ? "text-sky-300"
                          : "text-neutral-300"
                  }
                >
                  {URGENCY_LABEL[bucket]}
                </span>
                <span className="font-normal text-neutral-600">({buckets[bucket].length})</span>
              </h2>
              <Card className="divide-y divide-neutral-800/70 overflow-hidden">
                {buckets[bucket].map(({ row, urgency }) => (
                  <CalendarItem
                    key={`${row.kind}-${row.id}`}
                    row={row}
                    urgency={urgency}
                    slug={tenant.slug}
                    fundName={fundName}
                    lpName={lpName}
                    isMine={row.assigned_to != null && row.assigned_to === user?.id}
                  />
                ))}
              </Card>
            </section>
          ),
        )
      )}
    </>
  );
}

function CalendarItem({
  row,
  urgency,
  slug,
  fundName,
  lpName,
  isMine,
}: {
  row: CalendarRow;
  urgency: TaskUrgency;
  slug: string;
  fundName: (id: string | null) => string | null;
  lpName: (id: string | null) => string | null;
  isMine: boolean;
}) {
  const scopeLabel =
    row.scope === "fund"
      ? fundName(row.fund_id)
      : row.scope === "lp"
        ? lpName(row.lp_id)
        : "Firm-wide";

  const body = (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-900/50">
      <div className="w-24 shrink-0">
        <div className="text-sm tabular-nums text-neutral-200">{row.due_date ?? "—"}</div>
        <div className="text-[11px] text-neutral-500">{daysUntil(row.due_date)}</div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-neutral-100">{row.title}</span>
          {row.period_label && (
            <span className="shrink-0 text-[11px] text-neutral-500">· {row.period_label}</span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-neutral-500">
          <Badge tone="neutral">{AUTHORITY_LABEL[row.authority]}</Badge>
          <span>{CATEGORY_LABEL[row.category]}</span>
          <span>·</span>
          <span className="truncate">{scopeLabel}</span>
          {row.citation && <span className="truncate text-neutral-600">· {row.citation}</span>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {isMine && <Badge tone="brand">You</Badge>}
        <Badge tone={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</Badge>
        <Badge tone={URGENCY_TONE[urgency]}>
          <Dot tone={URGENCY_TONE[urgency]} /> {URGENCY_LABEL[urgency]}
        </Badge>
      </div>
    </div>
  );

  if (row.kind === "task") {
    return (
      <Link to="/t/$slug/compliance/$taskId" params={{ slug, taskId: row.id }} className="block">
        {body}
      </Link>
    );
  }
  // Side-letter obligation — lives on the obligations page.
  return (
    <Link to="/t/$slug/obligations" params={{ slug }} className="block">
      {body}
    </Link>
  );
}

function DigestResultBanner({ digest, onClose }: { digest: DigestResult; onClose: () => void }) {
  const [showPreview, setShowPreview] = useState(false);
  return (
    <>
      <div className="border-brand-900/50 bg-brand-950/30 text-brand-200 flex items-center gap-3 rounded-lg border px-3 py-2 text-xs">
        <Mail className="size-4 shrink-0" />
        <div className="flex-1">
          {digest.configured ? (
            digest.sent ? (
              <>
                Reminder digest sent to {digest.to} — {digest.overdue} overdue, {digest.dueSoon} due
                soon.
              </>
            ) : (
              <>{digest.reason ?? "Nothing due — no email sent."}</>
            )
          ) : (
            <>
              Email isn&apos;t configured yet (no Resend key). Previewing the digest unsent —{" "}
              {digest.overdue} overdue, {digest.dueSoon} due soon.
              {digest.preview && (
                <button
                  className="hover:text-brand-100 ml-1 underline"
                  onClick={() => setShowPreview(true)}
                >
                  View preview
                </button>
              )}
            </>
          )}
        </div>
        <button onClick={onClose} className="text-brand-400 hover:text-brand-200">
          <X className="size-3.5" />
        </button>
      </div>

      {showPreview && digest.preview && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4">
          <button
            aria-label="Close preview"
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowPreview(false)}
          />
          <div className="relative z-10 mt-[8vh] w-full max-w-2xl overflow-hidden rounded-xl border border-neutral-800 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-neutral-200 bg-neutral-50 px-4 py-2">
              <div className="text-xs text-neutral-600">
                <span className="font-medium text-neutral-800">Subject:</span>{" "}
                {digest.preview.subject}
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="text-neutral-500 hover:text-neutral-800"
              >
                <X className="size-4" />
              </button>
            </div>
            <div
              className="max-h-[70vh] overflow-y-auto p-5"
              dangerouslySetInnerHTML={{ __html: digest.preview.html }}
            />
          </div>
        </div>
      )}
    </>
  );
}

export function CalendarPage() {
  return (
    <TenantLayout>
      <CalendarInner />
    </TenantLayout>
  );
}
